import type { Dayjs } from 'dayjs'
import type { ConnectionTierTransition } from './connection-tier-live-kit.hook'
import type { ToastType } from '@shared/lib/toast'

import dayjs from 'dayjs'

import { useToast } from '@shared/lib/toast'

import { ConnectionTier } from './types/classroom/enums'
import { useRoomLiveKit } from './room-live-kit.hook'
import {
  CONNECTION_JOIN_SUPPRESSION_MS,
  CONNECTION_TIER_RANK,
  CONNECTION_NOTICE_RECOVERY_MS,
  CONNECTION_NOTICE_UNSTABLE_MS,
  CONNECTION_WARNING_THROTTLE_MS,
} from '../config/connection-quality'

export type ConnectionNoticeVariant = 'unstable' | 'weak' | 'lost' | 'recovered'

export type ConnectionNoticeSide = 'self' | 'peer'

interface ConnectionNoticeLiveKitReturn {
  handleTransition: (transition: ConnectionTierTransition) => void
  clearSide: (side: ConnectionNoticeSide) => void
  start: () => void
  stop: () => void
}

const TOAST_STAYS_UNTIL_RESOLVED = 0

const NOTICE_TOAST_TYPE: Record<ConnectionNoticeVariant, ToastType> = {
  unstable: 'warning',
  weak: 'error',
  lost: 'error',
  recovered: 'success',
}

const NOTICE_DURATION_MS: Record<ConnectionNoticeVariant, number> = {
  unstable: CONNECTION_NOTICE_UNSTABLE_MS,
  recovered: CONNECTION_NOTICE_RECOVERY_MS,
  weak: TOAST_STAYS_UNTIL_RESOLVED,
  lost: TOAST_STAYS_UNTIL_RESOLVED,
}

const WARNING_VARIANT: Partial<Record<ConnectionTier, ConnectionNoticeVariant>> = {
  [ConnectionTier.Unstable]: 'unstable',
  [ConnectionTier.Weak]: 'weak',
  [ConnectionTier.Lost]: 'lost',
}

export const useConnectionNoticeLiveKit = (): ConnectionNoticeLiveKitReturn => {
  const { remoteName } = useRoomLiveKit()
  const toast = useToast()

  let joinedAt: Dayjs | null = null
  let suppressionTimer: ReturnType<typeof setTimeout> | null = null
  const lastWarningAt = new Map<ConnectionNoticeSide, Dayjs>()
  const warnedSides = new Set<ConnectionNoticeSide>()
  const deferredBySide = new Map<ConnectionNoticeSide, ConnectionTierTransition>()
  const standingToastId = new Map<ConnectionNoticeSide, string>()

  const peerName = (): string => remoteName.value || 'Your tutor'

  const isWithinJoinSuppression = (): boolean =>
    !!joinedAt && dayjs().diff(joinedAt) < CONNECTION_JOIN_SUPPRESSION_MS

  const sideOf = (transition: ConnectionTierTransition): ConnectionNoticeSide =>
    (transition.isSelf ? 'self' : 'peer')

  const buildTitle = (variant: ConnectionNoticeVariant, isSelf: boolean): string => {
    if (isSelf) {
      return {
        unstable: 'Your connection is unstable',
        weak: 'Your internet is weak',
        lost: 'You have lost connection',
        recovered: 'You are back',
      }[variant]
    }

    return {
      unstable: `${peerName()} has an unstable connection`,
      weak: `${peerName()} has a weak connection`,
      lost: `${peerName()} lost connection`,
      recovered: `${peerName()} is back`,
    }[variant]
  }

  const buildBody = (variant: ConnectionNoticeVariant, isSelf: boolean): string => {
    if (variant === 'recovered') return 'Connection restored.'
    if (variant === 'lost') return 'Trying to reconnect…'

    if (variant === 'unstable') {
      return isSelf ? `${peerName()} may see your screen freeze.` : 'Their video may freeze for a moment.'
    }

    return isSelf
      ? 'We turned your video down so your voice stays clear.'
      : 'We paused their video so you can still hear each other.'
  }

  const dismissStanding = (side: ConnectionNoticeSide): void => {
    const id = standingToastId.get(side)
    if (!id) return

    standingToastId.delete(side)
    toast.dismiss(id)
  }

  const show = (variant: ConnectionNoticeVariant, side: ConnectionNoticeSide, isSelf: boolean): void => {
    dismissStanding(side)

    const duration = NOTICE_DURATION_MS[variant]
    const id = toast.add({
      type: NOTICE_TOAST_TYPE[variant],
      title: buildTitle(variant, isSelf),
      description: buildBody(variant, isSelf),
      duration,
      closable: duration === TOAST_STAYS_UNTIL_RESOLVED,
    })

    if (duration === TOAST_STAYS_UNTIL_RESOLVED) standingToastId.set(side, id)
  }

  const escalatesAnExistingWarning = (transition: ConnectionTierTransition): boolean =>
    !!WARNING_VARIANT[transition.previousTier]
    && CONNECTION_TIER_RANK[transition.tier] < CONNECTION_TIER_RANK[transition.previousTier]

  const handleWarning = (
    variant: ConnectionNoticeVariant,
    side: ConnectionNoticeSide,
    transition: ConnectionTierTransition,
  ): void => {
    const bypassesThrottle = standingToastId.has(side) || escalatesAnExistingWarning(transition)
    const lastWarning = lastWarningAt.get(side)
    const isThrottled = !bypassesThrottle
      && !!lastWarning
      && dayjs().diff(lastWarning) < CONNECTION_WARNING_THROTTLE_MS

    if (isThrottled) return

    show(variant, side, transition.isSelf)

    lastWarningAt.set(side, dayjs())
    warnedSides.add(side)
  }

  const handleRecovery = (side: ConnectionNoticeSide, isSelf: boolean): void => {
    dismissStanding(side)

    if (!warnedSides.has(side)) return

    warnedSides.delete(side)
    show('recovered', side, isSelf)
  }

  const announce = (transition: ConnectionTierTransition): void => {
    const side = sideOf(transition)
    const variant = WARNING_VARIANT[transition.tier]

    if (variant) {
      handleWarning(variant, side, transition)
      return
    }

    handleRecovery(side, transition.isSelf)
  }

  const clearSuppressionTimer = (): void => {
    if (suppressionTimer) clearTimeout(suppressionTimer)
    suppressionTimer = null
  }

  const deferPastJoinSuppression = (transition: ConnectionTierTransition): void => {
    deferredBySide.set(sideOf(transition), transition)

    if (suppressionTimer) return

    const remaining = Math.max(0, CONNECTION_JOIN_SUPPRESSION_MS - dayjs().diff(joinedAt))

    suppressionTimer = setTimeout(() => {
      suppressionTimer = null

      const deferred = [...deferredBySide.values()]
      deferredBySide.clear()
      deferred.forEach(announce)
    }, remaining)
  }

  const handleTransition = (transition: ConnectionTierTransition): void => {
    if (isWithinJoinSuppression()) {
      deferPastJoinSuppression(transition)
      return
    }

    announce(transition)
  }

  const clearSide = (side: ConnectionNoticeSide): void => {
    dismissStanding(side)
    warnedSides.delete(side)
    lastWarningAt.delete(side)
    deferredBySide.delete(side)
  }

  const forgetEverySide = (): void => {
    clearSuppressionTimer()
    clearSide('self')
    clearSide('peer')
  }

  const start = (): void => {
    forgetEverySide()
    joinedAt = dayjs()
  }

  return {
    handleTransition,
    clearSide,
    start,
    stop: forgetEverySide,
  }
}
